import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, FolderOpen, Upload,
  Building2, Stethoscope, Clock3, ShieldCheck, ShieldOff,
  Calendar, X, Activity, FileText,
} from "lucide-react";
import { useRbac } from "@/hooks/useRbac";
import { useModalities } from "@/contexts/ModalitiesContext";
import {
  extractAllowedStats,
  isDashboardAdminRole,
  isStatAllowed,
  PATIENT_ANATOMY_SUMMARY_ITEMS,
} from "@/lib/dashboardStats";
import { getModalityChartColor } from "@/lib/modalities";
import StatCard from "@/components/StatCard";
import ManufacturerSankeyCard from "@/components/ManufacturerSankeyCard";
import AnonymizationCard from "@/components/AnonymizationCard";
import {
  StudiesCreativeCard,
  PatientsCreativeCard,
  NetworkCreativeCard,
  CtRatioCreativeCard,
} from "@/components/CreativeStatCards";
import { PageHeaderActionsRight } from "@/contexts/PageHeaderContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatModalityName = (key) =>
  key?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? "Unknown";

const DEFAULT_STATS = {
  patients: 0,
  studies: 0,
  files: 0,
  users: 0,
  organizations: 0,
  doctors: 0,
  total_cts: 0,
  today_cts: 0,
  week_cts: 0,
  month_cts: 0,
  status_uploaded: 0,
  status_in_review: 0,
  status_report_ready: 0,
  anonymized_patients: 0,
  unanonymized_patients: 0,
  pending_patients: 0,
  ct_pending_report: {
    ct_images_patients: 0,
    ct_report_patients: 0,
    awaiting_report: 0,
  },
  modality_distribution: [],
  valve_manufacturer_summary: {
    medtronic: 0,
    competition: 0,
  },
  patient_anatomy_summary: Object.fromEntries(
    PATIENT_ANATOMY_SUMMARY_ITEMS.map(({ key }) => [key, 0]),
  ),
};

const normalizePatientAnatomySummary = (summary = {}) =>
  Object.fromEntries(
    PATIENT_ANATOMY_SUMMARY_ITEMS.map(({ key }) => [key, summary?.[key] ?? 0]),
  );

const toCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizeCtPendingReport = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      ct_images_patients: toCount(value.ct_images_patients),
      ct_report_patients: toCount(value.ct_report_patients),
      awaiting_report: toCount(value.awaiting_report),
    };
  }

  const pending = toCount(value);
  return {
    ct_images_patients: 0,
    ct_report_patients: 0,
    awaiting_report: pending,
  };
};

const normalizeStats = (data = {}) => ({
  ...DEFAULT_STATS,
  ...data,
  studies: data.studies ?? data.total_cts ?? 0,
  modality_distribution: data.modality_distribution ?? [],
  valve_manufacturer_summary: {
    medtronic: data.valve_manufacturer_summary?.medtronic ?? 0,
    competition: data.valve_manufacturer_summary?.competition ?? 0,
  },
  patient_anatomy_summary: normalizePatientAnatomySummary(data.patient_anatomy_summary),
  pending_patients: data.pending_patients ?? data.pendingStudies ?? 0,
  ct_pending_report: normalizeCtPendingReport(data.ct_pending_report),
});

const ModalityPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-popover-foreground shadow-md">
      <p className="text-xs font-medium">{item.name}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums">
        {item.value}{" "}
        <span className="font-normal text-muted-foreground">
          ({item.percent}%)
        </span>
      </p>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { role, canUpload, canFilterHospital, canFilterAllUsers } = useRbac();
  const { getModality } = useModalities();
  const [stats, setStats] = useState(normalizeStats());
  const [allowedStats, setAllowedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [uploadedById, setUploadedById] = useState("");
  const [hospitals, setHospitals] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);

  const isAdmin = isDashboardAdminRole(role);
  const showHospitalFilter = canFilterHospital;
  const showUserFilter = canFilterAllUsers;
  const allow = (key) => isStatAllowed(allowedStats, key);
  const hasActiveFilters = Boolean(
    dateFrom || dateTo || (showHospitalFilter && hospitalId) || (showUserFilter && uploadedById),
  );
  const selectedUser = users.find((u) => u?.id === uploadedById);

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setHospitalId("");
    setUploadedById("");
  };

  const handleHospitalChange = (value) => {
    const nextHospitalId = value === "all" ? "" : value;
    setHospitalId(nextHospitalId);
    setUploadedById("");
  };

  const handleModalityNavigate = (modalityKey) => {
    if (!modalityKey || String(modalityKey).startsWith("modality-")) return;
    navigate(`/patients?study_types=${encodeURIComponent(modalityKey)}`);
  };

  const handlePatientsNavigate = (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.anonymized != null && filters.anonymized !== "") {
      params.set("anonymized", String(filters.anonymized));
    }
    if (filters.status) params.set("status", filters.status);
    if (filters.organization) params.set("organization", filters.organization);
    if (filters.doctor) params.set("doctor", filters.doctor);
    if (filters.valve_manufacturer) {
      params.set("valve_manufacturer", filters.valve_manufacturer);
    }
    const qs = params.toString();
    navigate(qs ? `/patients?${qs}` : "/patients");
  };

  const patientsNavByStatKey = {
    patients: () => handlePatientsNavigate(),
    pending_patients: () => handlePatientsNavigate({ status: "pending" }),
    completed_patients: () => handlePatientsNavigate({ status: "completed" }),
    anonymized_patients: () => handlePatientsNavigate({ anonymized: true }),
    unanonymized_patients: () => handlePatientsNavigate({ anonymized: false }),
    organizations: () => navigate("/organizations?tab=hospital"),
    doctors: () => navigate("/users?role=hospital_doctor"),
    medtronic: () => handlePatientsNavigate({ valve_manufacturer: "medtronic" }),
    competition: () => handlePatientsNavigate({ valve_manufacturer: "competition" }),
  };

  const getCardClickHandler = (card) => {
    if (card?.items?.length) return undefined;
    return patientsNavByStatKey[card?.key];
  };

  const handleManufacturerClick = (item) => {
    if (!item?.key) return;
    handlePatientsNavigate({
      valve_manufacturer: item.key === "medtronic" ? "medtronic" : "competition",
    });
  };

  const modalityChart = useMemo(() => {
    const items = [...(stats.modality_distribution ?? [])].sort(
      (a, b) => (b.count ?? 0) - (a.count ?? 0),
    );
    const total = items.reduce((sum, item) => sum + (item.count ?? 0), 0);

    const data = items.map((item, i) => {
      const value = item.count ?? 0;
      const name = getModality(item.modality)?.label ?? formatModalityName(item.modality);
      return {
        key: item.modality ?? `modality-${i}`,
        name,
        value,
        percent: total > 0 ? Math.round((value / total) * 100) : 0,
        color: getModalityChartColor(item.modality),
      };
    });

    return { data, total };
  }, [stats.modality_distribution, getModality]);

  const statCards = useMemo(() => {
    const cards = [];

    if (allow("ct_pending_report")) {
      const ratio = stats.ct_pending_report ?? {};
      cards.push({
        key: "ct_pending_report",
        title: "CT Ratio",
        variant: "creative-ct-ratio",
        icon: FileText,
        glowClass: "stat-glow-amber",
        images: ratio.ct_images_patients ?? 0,
        reports: ratio.ct_report_patients ?? 0,
      });
    }

    if (allow("total_cts")) {
      cards.push({
        key: "total_cts",
        title: "Studies",
        variant: "creative-studies",
        value: stats.total_cts,
        today: stats.today_cts,
        week: stats.week_cts,
        month: stats.month_cts,
        icon: FolderOpen,
        glowClass: "stat-glow-green",
      });
    }

    {
      const showPatients = allow("patients");
      const showPending = allow("pending_patients");
      const completedPatients = Math.max(0, (stats.patients ?? 0) - (stats.pending_patients ?? 0));
      if (showPatients && showPending) {
        cards.push({
          key: "patients_summary",
          title: "Patients",
          variant: "creative-patients",
          icon: Users,
          glowClass: "stat-glow-indigo",
          total: stats.patients,
          pending: stats.pending_patients,
          completed: completedPatients,
          onTotalClick: patientsNavByStatKey.patients,
          onPendingClick: patientsNavByStatKey.pending_patients,
          onCompletedClick: patientsNavByStatKey.completed_patients,
        });
      } else if (showPatients) {
        cards.push({
          key: "patients",
          title: "Patients",
          value: stats.patients,
          icon: Users,
          glowClass: "stat-glow-indigo",
        });
      } else if (showPending) {
        cards.push({
          key: "pending_patients",
          title: "Pending Patients",
          value: stats.pending_patients,
          icon: Clock3,
          glowClass: "stat-glow-indigo",
        });
      }
    }

    {
      const showOrgs = allow("organizations");
      const showDoctors = allow("doctors");
      if (showOrgs && showDoctors) {
        cards.push({
          key: "network_summary",
          title: "Network",
          variant: "creative-network",
          icon: Building2,
          glowClass: "stat-glow-red",
          hospitals: stats.organizations,
          doctors: stats.doctors,
          onHospitalsClick: patientsNavByStatKey.organizations,
          onDoctorsClick: patientsNavByStatKey.doctors,
        });
      } else if (showOrgs) {
        cards.push({
          key: "organizations",
          title: "Hospitals",
          value: stats.organizations,
          icon: Building2,
          glowClass: "stat-glow-red",
        });
      } else if (showDoctors) {
        cards.push({
          key: "doctors",
          title: "Doctors",
          value: stats.doctors,
          icon: Stethoscope,
          glowClass: "stat-glow-sky",
        });
      }
    }

    {
      const showAnonymized = allow("anonymized_patients");
      const showUnanonymized = allow("unanonymized_patients");
      if (showAnonymized && showUnanonymized) {
        cards.push({
          key: "patient_anonymization",
          title: "Anonymization",
          variant: "anonymization",
          icon: ShieldCheck,
          glowClass: "stat-glow-green",
          anonymized: stats.anonymized_patients,
          identified: stats.unanonymized_patients,
          pending: stats.pending_patients,
          onAnonymizedClick: patientsNavByStatKey.anonymized_patients,
          onIdentifiedClick: patientsNavByStatKey.unanonymized_patients,
          onPendingClick: allow("pending_patients")
            ? patientsNavByStatKey.pending_patients
            : undefined,
        });
      } else if (showAnonymized) {
        cards.push({
          key: "anonymized_patients",
          title: "Anonymized Patients",
          value: stats.anonymized_patients,
          icon: ShieldCheck,
          glowClass: "stat-glow-green",
        });
      } else if (showUnanonymized) {
        cards.push({
          key: "unanonymized_patients",
          title: "Unanonymized Patients",
          value: stats.unanonymized_patients,
          icon: ShieldOff,
          glowClass: "stat-glow-red",
        });
      }
    }

    if (allow("patient_anatomy_summary")) {
      cards.push({
        key: "patient_anatomy_summary",
        title: "Implants",
        icon: Activity,
        glowClass: "stat-glow-sky",
        items: PATIENT_ANATOMY_SUMMARY_ITEMS.map(({ key, title }) => ({
          key,
          title,
          value: stats.patient_anatomy_summary?.[key] ?? 0,
        })),
      });
    }

    return cards;
  }, [stats, allowedStats]);

  const compactStatCards = statCards.filter((card) => card.key !== "patient_anatomy_summary");
  const anatomyManufacturerCard = statCards.find((card) => card.key === "patient_anatomy_summary");

  const useWeightedRow = compactStatCards.length >= 5;
  const statsCols = useWeightedRow
    ? "lg:grid-cols-[0.9fr_1.2fr_1fr_1fr_1fr]"
    : compactStatCards.length === 4
      ? "lg:grid-cols-4"
      : compactStatCards.length === 2
        ? "lg:grid-cols-2"
        : "lg:grid-cols-3";

  useEffect(() => {
    const init = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (_) {
        /* seed is admin-only; ignore for other roles */
      }

      const configUrl = isAdmin
        ? `${API}/constants/dashboard-stats`
        : `${API}/constants/dashboard-stats/users`;

      try {
        const configRes = await axios.get(configUrl);
        setAllowedStats(extractAllowedStats(configRes.data));
      } catch (err) {
        console.error("Failed to load dashboard card config", err);
        setAllowedStats(null);
      }
    };
    init();
  }, [isAdmin]);

  useEffect(() => {
    if (!showHospitalFilter) {
      setHospitals([]);
      setHospitalId("");
      setFilterOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadHospitals = async () => {
      setFilterOptionsLoading(true);
      try {
        const orgsRes = await axios.get(`${API}/organizations`, { params: { page: 1, limit: 100 } });
        const items = orgsRes.data?.organizations ?? orgsRes.data?.items ?? [];
        const hospitalList = (Array.isArray(items) ? items : [])
          .filter((org) => org?.id)
          .filter((org) => {
            const type = String(org?.type ?? "").trim().toLowerCase();
            return !type || type === "hospital";
          });
        if (!cancelled) setHospitals(hospitalList);
      } catch (err) {
        console.error("Failed to load dashboard hospitals", err);
        if (!cancelled) setHospitals([]);
      } finally {
        if (!cancelled) setFilterOptionsLoading(false);
      }
    };
    loadHospitals();
    return () => { cancelled = true; };
  }, [showHospitalFilter]);

  useEffect(() => {
    if (!showUserFilter) {
      setUsers([]);
      setUploadedById("");
      return undefined;
    }

    let cancelled = false;
    const loadUsers = async () => {
      try {
        const usersRes = await axios.get(`${API}/users`, {
          params: {
            page: 1,
            limit: 200,
            organization_id: hospitalId || undefined,
          },
        });
        const items = usersRes.data?.users ?? usersRes.data?.items ?? [];
        if (!cancelled) {
          setUsers(Array.isArray(items) ? items.filter((u) => u?.id) : []);
        }
      } catch (err) {
        console.error("Failed to load dashboard users", err);
        if (!cancelled) setUsers([]);
      }
    };
    loadUsers();
    return () => { cancelled = true; };
  }, [hospitalId, showUserFilter]);

  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/stats`, {
          params: {
            hospital_id: showHospitalFilter ? (hospitalId || undefined) : undefined,
            uploaded_by_id: showUserFilter ? (uploadedById || undefined) : undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          },
        });
        if (!cancelled) setStats(normalizeStats(res.data ?? {}));
      } catch (err) {
        console.error("Failed to load dashboard stats", err);
        if (!cancelled) setStats(normalizeStats());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadStats();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, hospitalId, uploadedById, showHospitalFilter, showUserFilter]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="dashboard-page">
      <PageHeaderActionsRight>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {showHospitalFilter && (
            <Select
              value={hospitalId || "all"}
              onValueChange={handleHospitalChange}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger
                data-testid="dashboard-filter-hospital"
                className="h-10 w-full sm:w-48 border-[var(--xl-line2)] bg-white"
                aria-label="Filter by hospital"
              >
                <SelectValue placeholder={filterOptionsLoading ? "Loading hospitals…" : "Hospital"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All hospitals</SelectItem>
                {hospitalId && !hospitals.some((org) => String(org.id) === hospitalId) ? (
                  <SelectItem value={hospitalId}>{hospitalId}</SelectItem>
                ) : null}
                {hospitals.map((org) => (
                  <SelectItem key={org.id} value={String(org.id)}>
                    {org.name ?? org.organization_name ?? org.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showUserFilter && (
            <Select
              value={uploadedById || "all"}
              onValueChange={(v) => setUploadedById(v === "all" ? "" : v)}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger
                data-testid="dashboard-filter-user"
                className="h-10 w-full sm:w-44 border-[var(--xl-line2)] bg-white"
                aria-label="Filter by user"
              >
                <SelectValue placeholder={filterOptionsLoading ? "Loading users…" : "User"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All users</SelectItem>
                {uploadedById && !selectedUser ? (
                  <SelectItem value={uploadedById}>{uploadedById}</SelectItem>
                ) : null}
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name || u.email || u.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Calendar className="w-3.5 h-3.5 shrink-0 text-[var(--xl-mut2)]" aria-hidden />
            <Input
              data-testid="dashboard-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              className="h-10 w-[9.5rem] sm:w-36 text-sm border-[var(--xl-line2)] bg-white"
              aria-label="Stats from date"
            />
            <span className="text-xs text-[var(--xl-mut)] shrink-0">to</span>
            <Input
              data-testid="dashboard-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              className="h-10 w-[9.5rem] sm:w-36 text-sm border-[var(--xl-line2)] bg-white"
              aria-label="Stats to date"
            />
          </div>
          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              data-testid="dashboard-clear-filters"
              className="h-10 gap-1 text-xs text-[var(--xl-mut)]"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </Button>
          )}
        </div>
      </PageHeaderActionsRight>

      <div data-testid="dashboard-stats" className="shrink-0 space-y-3">
        {compactStatCards.length > 0 && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 items-stretch gap-3 ${statsCols}`}>
            {compactStatCards.map((card, i) => {
              const delay = `animate-fade-in-delay-${(i % 4) + 1}`;
              if (card.variant === "anonymization") {
                return (
                  <AnonymizationCard
                    key={card.key}
                    anonymized={card.anonymized}
                    identified={card.identified}
                    pending={card.pending}
                    loading={loading}
                    delay={delay}
                    onAnonymizedClick={card.onAnonymizedClick}
                    onIdentifiedClick={card.onIdentifiedClick}
                    onPendingClick={card.onPendingClick}
                    className="h-full"
                  />
                );
              }

              if (card.variant === "creative-studies") {
                return (
                  <StudiesCreativeCard
                    key={card.key}
                    value={card.value}
                    loading={loading}
                    delay={delay}
                    icon={card.icon}
                    glowClass={card.glowClass}
                  />
                );
              }

              if (card.variant === "creative-patients") {
                return (
                  <PatientsCreativeCard
                    key={card.key}
                    total={card.total}
                    pending={card.pending}
                    completed={card.completed}
                    loading={loading}
                    delay={delay}
                    icon={card.icon}
                    glowClass={card.glowClass}
                    onTotalClick={card.onTotalClick}
                    onPendingClick={card.onPendingClick}
                    onCompletedClick={card.onCompletedClick}
                  />
                );
              }

              if (card.variant === "creative-network") {
                return (
                  <NetworkCreativeCard
                    key={card.key}
                    hospitals={card.hospitals}
                    doctors={card.doctors}
                    loading={loading}
                    delay={delay}
                    icon={card.icon}
                    glowClass={card.glowClass}
                    onHospitalsClick={card.onHospitalsClick}
                    onDoctorsClick={card.onDoctorsClick}
                  />
                );
              }

              if (card.variant === "creative-ct-ratio") {
                return (
                  <CtRatioCreativeCard
                    key={card.key}
                    images={card.images}
                    reports={card.reports}
                    loading={loading}
                    delay={delay}
                    icon={card.icon}
                    glowClass={card.glowClass}
                  />
                );
              }

              return (
                <StatCard
                  key={card.key}
                  title={card.title}
                  value={card.value}
                  items={card.items}
                  icon={card.icon}
                  glowClass={card.glowClass}
                  delay={delay}
                  loading={loading}
                  onClick={getCardClickHandler(card)}
                  testIdPrefix="dashboard-stat"
                />
              );
            })}
          </div>
        )}
      </div>

      <div
        className={`grid min-h-0 flex-[0.95] grid-cols-1 gap-3 ${
          allow("modality_distribution") || anatomyManufacturerCard ? "lg:grid-cols-2" : ""
        }`}
      >
        <div className="flex h-full min-h-0 min-w-0 flex-col gap-3">
          {allow("modality_distribution") && (
            <Card className="animate-fade-in flex min-h-0 min-w-0 flex-1 flex-col" data-testid="modality-list">
              <CardHeader className="shrink-0 space-y-0 px-4 pb-1 pt-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Studies by Modality</CardTitle>
              </CardHeader>
              <CardContent className="flex min-h-0 flex-1 flex-col px-4 pb-3 pt-0">
                {(modalityChart.data?.length ?? 0) > 0 ? (
                  <div className="flex min-h-0 flex-1 flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-2">
                    <div className="relative h-[15.2rem] w-full min-h-[15.2rem] max-w-[280px] shrink-0 sm:h-full sm:min-h-[16.15rem] sm:max-w-none sm:flex-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={modalityChart.data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="58%"
                            outerRadius="88%"
                            paddingAngle={3}
                            cornerRadius={6}
                            strokeWidth={0}
                            style={{ cursor: "pointer" }}
                            onClick={(slice) => handleModalityNavigate(slice?.payload?.key ?? slice?.key)}
                          >
                            {modalityChart.data.map((entry) => (
                              <Cell key={entry.key} fill={entry.color} className="cursor-pointer outline-none" />
                            ))}
                          </Pie>
                          <Tooltip content={<ModalityPieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold tabular-nums leading-none sm:text-4xl">
                          {modalityChart.total}
                        </span>
                        <span className="mt-1 text-xs font-medium text-muted-foreground">
                          Total
                        </span>
                      </div>
                    </div>

                    <ul className="flex w-full flex-col justify-center gap-0.5 sm:w-[13.5rem] sm:shrink-0">
                      {modalityChart.data.map((item, i) => (
                        <li key={item.key}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-1.5 rounded-lg px-1 py-1 text-left transition-colors hover:bg-muted/50"
                            data-testid={`modality-${i}`}
                            onClick={() => handleModalityNavigate(item.key)}
                            aria-label={`View patients with ${item.name} studies`}
                          >
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: item.color }}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 truncate text-sm">{item.name}</span>
                            <span className="shrink-0 text-sm font-semibold tabular-nums">{item.value}</span>
                            <span className="w-8 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                              {item.percent}%
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="py-6 flex items-center justify-center text-sm text-muted-foreground">
                    {loading ? "Loading..." : "No data yet"}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="animate-fade-in min-w-0 shrink-0" data-testid="quick-actions">
            <CardHeader className="space-y-0 px-4 pb-1 pt-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="flex flex-wrap gap-3">
                {canUpload && (
                  <Button
                    data-testid="quick-upload"
                    onClick={() => navigate("/upload", { state: { from: "/" } })}
                    className="gap-2"
                  >
                    <Upload className="w-4 h-4" /> Upload New Patient
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {anatomyManufacturerCard && (
          <div className="h-full min-h-[28rem]">
            <ManufacturerSankeyCard
              items={anatomyManufacturerCard.items}
              loading={loading}
              delay="animate-fade-in-delay-2"
              onItemClick={handleManufacturerClick}
              fillParent
              className="h-full min-h-[28rem]"
            />
          </div>
        )}
      </div>
    </div>
  );
}
